/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Image,
  FlatList,
  ActivityIndicator,
  RefreshControl,
  TextInput,
  Modal,
  Alert,
  Linking,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { apiFetch } from '../../utils/apiClient';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const SKILL_LEVELS = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];
const LEVEL_WEIGHT = { Expert: 1.0, Advanced: 0.85, Intermediate: 0.65, Beginner: 0.45 };

// Multi-factor match weights — must sum to 1.0
const MATCH_WEIGHTS = {
  skills:     0.40,
  role:       0.20,
  experience: 0.15,
  location:   0.15,
  salary:     0.10,
};

const EDUCATION_LEVEL_RANK = {
  'high school': 1, 'associate': 2, 'bachelor': 3, 'bachelors': 3,
  'master': 4, 'masters': 4, 'mba': 4, 'phd': 5, 'doctorate': 5,
};

const CATEGORY_KEYWORDS = {
  engineering: ['engineer', 'mechanical', 'electrical', 'civil', 'structural', 'aerospace', 'cad'],
  technology:  ['developer', 'software', 'frontend', 'backend', 'fullstack', 'data', 'devops', 'cloud', 'ml', 'ai ', 'security'],
  management:  ['manager', 'lead', 'director', 'head of', 'pm', 'product owner', 'scrum'],
  design:      ['designer', 'ux', 'ui', 'graphic', 'creative', 'art'],
};

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const normalize = (s) => String(s || '').toLowerCase().trim();

const inferCategory = (title = '', department = '') => {
  const t = `${title} ${department}`.toLowerCase();
  for (const [cat, kws] of Object.entries(CATEGORY_KEYWORDS)) {
    if (kws.some(kw => t.includes(kw))) return cat;
  }
  return 'other';
};

const formatRelativeDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 60) return diffMin <= 1 ? 'Just now' : `${diffMin} min ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 30) return `${diffD}d ago`;
  return `${Math.floor(diffD / 30)}mo ago`;
};

const formatSalary = (job) => {
  const min = job.salary_min ?? job.min_salary;
  const max = job.salary_max ?? job.max_salary;
  const cur = job.salary_currency || job.currency || '£';
  if (min && max) return `${cur}${Number(min).toLocaleString()} - ${cur}${Number(max).toLocaleString()}`;
  if (min) return `From ${cur}${Number(min).toLocaleString()}`;
  if (max) return `Up to ${cur}${Number(max).toLocaleString()}`;
  if (job.salary_range) return job.salary_range;
  if (job.salary) return String(job.salary);
  return 'Not specified';
};

const extractSkills = (job) => {
  const raw = job.skills_required || job.required_skills || job.skills || job.skill_requirements || [];
  if (Array.isArray(raw)) return raw.map(String).filter(Boolean);
  if (typeof raw === 'string') {
    try { const p = JSON.parse(raw); if (Array.isArray(p)) return p.map(String); } catch {}
    return raw.split(',').map(s => s.trim()).filter(Boolean);
  }
  return [];
};

// Token-set similarity for short strings (titles, locations).
// Returns 0..1.
const tokenSetSimilarity = (a, b) => {
  const tokenize = s => new Set(
    normalize(s).split(/[^a-z0-9]+/).filter(t => t && t.length > 1)
  );
  const A = tokenize(a), B = tokenize(b);
  if (!A.size || !B.size) return 0;
  let intersect = 0;
  for (const t of A) if (B.has(t)) intersect++;
  return intersect / Math.max(A.size, B.size);
};

// Pull the most distinctive keyword from a job title for API pre-filtering.
// "Senior Mechanical Engineer" → "mechanical"
// "Software Developer (Backend)" → "developer"
// "VP Marketing" → "marketing"
// We strip seniority noise so we don't end up filtering by "senior".
const TITLE_STOPWORDS = new Set([
  'senior','junior','lead','principal','staff','head','chief','vp','vice','president','assistant',
  'associate','intern','trainee','entry','mid','level','executive','officer','of','the','and','for',
  'with','to','at','in','on','i','ii','iii','iv','v','sr','jr','remote','hybrid','onsite','full',
  'part','time','contract','permanent','temporary','freelance'
]);

const extractTitleKeyword = (title) => {
  if (!title) return null;
  const tokens = normalize(title)
    .split(/[^a-z0-9]+/)
    .filter(t => t && t.length > 2 && !TITLE_STOPWORDS.has(t));
  if (!tokens.length) return null;
  // Prefer the longest distinctive token (usually the specialty)
  tokens.sort((a, b) => b.length - a.length);
  return tokens[0];
};

const educationRankOf = (entry) => {
  const txt = normalize(entry.degree || entry.level || entry.qualification);
  for (const [key, rank] of Object.entries(EDUCATION_LEVEL_RANK)) {
    if (txt.includes(key)) return rank;
  }
  return 3; // assume bachelor if unspecified
};

// ─────────────────────────────────────────────────────────────────────────────
// PROFILE NORMALIZER
// Build a stable shape regardless of what ProfileManagementService returns.
// ─────────────────────────────────────────────────────────────────────────────
// Derive total years of professional experience from the experience entries'
// date ranges (merges overlapping roles so concurrent jobs don't double-count).
// Fallback for when no explicit years field is stored on the profile.
const computeYearsFromExperience = (experiences = []) => {
  if (!Array.isArray(experiences) || !experiences.length) return 0;

  // Robust date parser: handles ISO, year-only ("2011"), month-year
  // ("Jan 2011", "01/2011", "2011-03"), and falls back to Date parsing.
  const parseDate = (v) => {
    if (v == null) return null;
    if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
    const s = String(v).trim();
    if (!s) return null;
    // Pure year, e.g. "2011"
    if (/^\d{4}$/.test(s)) return new Date(Number(s), 0, 1);
    // Year-month with separator, e.g. "2011-03", "2011/03"
    let m = s.match(/^(\d{4})[-/](\d{1,2})/);
    if (m) return new Date(Number(m[1]), Math.max(0, Number(m[2]) - 1), 1);
    // Month-year, e.g. "03/2011"
    m = s.match(/^(\d{1,2})[-/](\d{4})$/);
    if (m) return new Date(Number(m[2]), Math.max(0, Number(m[1]) - 1), 1);
    // "Jan 2011" / "January 2011"
    const months = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
    m = s.toLowerCase().match(/([a-z]{3,})\s+(\d{4})/);
    if (m) {
      const mi = months.indexOf(m[1].slice(0, 3));
      if (mi >= 0) return new Date(Number(m[2]), mi, 1);
    }
    // Fallback to native parsing (handles full ISO timestamps)
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  };

  const isPresent = (v) => {
    if (!v) return true; // empty end date = still there
    const s = String(v).trim().toLowerCase();
    return s === 'present' || s === 'current' || s === 'now' || s === 'ongoing';
  };

  const now = new Date();
  const intervals = [];
  for (const e of experiences) {
    const start = parseDate(e.start_date || e.startDate || e.from || e.start || e.startYear || e.start_year);
    if (!start) continue;
    const rawEnd = e.end_date ?? e.endDate ?? e.to ?? e.end ?? e.endYear ?? e.end_year;
    const flaggedCurrent = !!(e.current || e.is_current || e.present || e.isCurrent || e.currently_working);
    const end = (flaggedCurrent || isPresent(rawEnd)) ? now : (parseDate(rawEnd) || now);
    if (end < start) continue;
    intervals.push([start.getTime(), end.getTime()]);
  }
  if (!intervals.length) return 0;
  intervals.sort((a, b) => a[0] - b[0]);
  const merged = [intervals[0].slice()];
  for (let i = 1; i < intervals.length; i++) {
    const last = merged[merged.length - 1];
    if (intervals[i][0] <= last[1]) last[1] = Math.max(last[1], intervals[i][1]);
    else merged.push(intervals[i].slice());
  }
  const totalMs = merged.reduce((sum, [s, e]) => sum + (e - s), 0);
  const years = Math.round(totalMs / (1000 * 60 * 60 * 24 * 365.25));
  try { console.log('[YearsCalc]', experiences.length, 'entries →', years, 'yrs', experiences.map(e => `${e.start_date||e.startDate||'?'}→${e.end_date||e.endDate||(e.current?'present':'?')}`)); } catch {}
  return years;
};

const normalizeProfile = (raw) => {
  if (!raw || typeof raw !== 'object') {
    return {
      id: null, userId: null, name: '', email: '', phone: '', photo: null,
      headline: '', summary: '',
      currentTitle: '', currentCompany: '',
      yearsOfExperience: 0,
      location: '', city: '', country: '',
      preferredLocations: [], preferredJobTypes: [],
      willingToRelocate: false, openToRemote: true,
      expectedSalaryMin: 0, expectedSalaryMax: 0,
      linkedinUrl: '', githubUrl: '', portfolioUrl: '', resumeUrl: '', websiteUrl: '',
      skills: [], experience: [], education: [], certifications: [],
      languages: [], projects: [], awards: [],
    };
  }
  const p = raw;
  const skills = Array.isArray(p.skills) ? p.skills.map((s, i) => {
    if (typeof s === 'string') return { id: String(i + 1), name: s, level: 'Intermediate' };
    return {
      id:    String(s.id || i + 1),
      name:  s.name || s.title || s.skill_name || 'Skill',
      level: s.level || s.proficiency_level || 'Intermediate',
    };
  }) : [];

  return {
    id:                p.id || p.user_id || null,
    userId:            p.user_id || p.id || null,
    name:              p.name || p.full_name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || '',
    email:             p.email || '',
    phone:             p.phone || p.phone_number || '',
    photo:             p.photo || p.profile_photo || p.avatar_url || null,
    headline:          p.headline || p.title || '',
    summary:           p.summary || p.about || p.bio || '',
    currentTitle:      p.current_title || p.job_title || p.position || '',
    currentCompany:    p.current_company || p.company || '',
    yearsOfExperience: Number(p.years_of_experience ?? p.experience_years ?? p.total_experience ?? p.totalYears ?? p.total_years ?? p.yearsOfExperience ?? 0)
                         || computeYearsFromExperience(Array.isArray(p.experience) ? p.experience : (Array.isArray(p.experiences) ? p.experiences : (Array.isArray(p.work_experience) ? p.work_experience : []))),
    location:          p.location || [p.city, p.country].filter(Boolean).join(', ') || '',
    city:              p.city || '',
    country:           p.country || '',
    preferredLocations: Array.isArray(p.preferred_locations) ? p.preferred_locations : [],
    preferredJobTypes:  Array.isArray(p.preferred_job_types) ? p.preferred_job_types : [],
    willingToRelocate:  !!(p.willing_to_relocate ?? p.relocate),
    openToRemote:       p.open_to_remote === false ? false : true,
    expectedSalaryMin:  Number(p.expected_salary_min ?? 0) || 0,
    expectedSalaryMax:  Number(p.expected_salary_max ?? p.expected_salary ?? 0) || 0,
    linkedinUrl:        p.linkedin_url || p.linkedin || '',
    githubUrl:          p.github_url || p.github || '',
    portfolioUrl:       p.portfolio_url || p.portfolio || '',
    resumeUrl:          p.resume_url || p.cv_url || '',
    websiteUrl:         p.website_url || p.website || '',
    skills,
    experience:     Array.isArray(p.experience) ? p.experience : (Array.isArray(p.experiences) ? p.experiences : (Array.isArray(p.work_experience) ? p.work_experience : [])),
    education:      Array.isArray(p.education) ? p.education : (Array.isArray(p.educations) ? p.educations : []),
    certifications: Array.isArray(p.certifications) ? p.certifications : (Array.isArray(p.certificates) ? p.certificates : []),
    languages:      Array.isArray(p.languages) ? p.languages : [],
    projects:       Array.isArray(p.projects) ? p.projects : [],
    awards:         Array.isArray(p.awards) ? p.awards : [],
  };
};

// Profile completion 0..100 — used in the header strip and for nudging the user.
const computeProfileCompleteness = (profile) => {
  if (!profile) return 0;
  const has = (v) => !!String(v ?? '').trim();
  // Mirror ProfileScreen.calcCompletion: 7 core fields + photo, then bonuses.
  // (Mapped to the normalized profile shape used here.)
  const coreFields = [
    profile.name,
    profile.email,
    profile.phone,
    profile.location || profile.city || profile.country,
    profile.currentTitle,
    profile.currentCompany,
    profile.summary,
  ];
  let done = coreFields.filter(has).length;
  const total = coreFields.length + 1; // +1 for photo
  if (profile.photo) done++;

  let bonus = 0;
  if ((profile.experience || []).length > 0) bonus += 8;
  if ((profile.education || []).length > 0) bonus += 5;
  const sk = (profile.skills || []).length;
  if (sk >= 5) bonus += 10; else if (sk > 0) bonus += 4;
  if ((profile.certifications || []).length > 0) bonus += 4;
  if ((profile.projects || []).length > 0) bonus += 3;
  if ((profile.languages || []).length > 0) bonus += 3;
  if (has(profile.linkedinUrl)) bonus += 5;

  const pct = Math.round((done / total) * 100) + bonus;
  return Math.max(0, Math.min(100, pct));
};

// ─────────────────────────────────────────────────────────────────────────────
// MULTI-FACTOR MATCH SCORING
// Returns { overall, breakdown: { skills, role, experience, location, salary }, notes }
// ─────────────────────────────────────────────────────────────────────────────
const scoreSkills = (jobSkills, userSkills) => {
  if (!jobSkills.length) return { score: 50, note: 'No skills listed on the job — partial credit.' };
  if (!userSkills.length) return { score: 0, note: 'Add skills to your profile to see this score.' };
  const userMap = new Map(userSkills.map(s => [normalize(s.name), LEVEL_WEIGHT[s.level] || 0.5]));
  let total = 0, matched = 0;
  for (const sk of jobSkills) {
    const w = userMap.get(normalize(sk));
    if (w) { total += w; matched++; }
  }
  const score = Math.round((total / jobSkills.length) * 100);
  return {
    score: Math.max(0, Math.min(100, score)),
    note: `Matched ${matched}/${jobSkills.length} required skills.`,
  };
};

const scoreRole = (jobTitle, profile) => {
  if (!profile.currentTitle && !profile.experience.length) {
    return { score: 30, note: 'Add a current role to improve this score.' };
  }
  let best = tokenSetSimilarity(jobTitle, profile.currentTitle);
  for (const exp of profile.experience.slice(0, 5)) {
    const sim = tokenSetSimilarity(jobTitle, exp.title || exp.position || exp.job_title);
    if (sim > best) best = sim;
  }
  const score = Math.round(best * 100);
  if (score >= 70) return { score, note: 'Your role history closely matches this job title.' };
  if (score >= 40) return { score, note: 'Your role history is related to this job title.' };
  return { score: Math.max(score, 20), note: 'Your role history is in a different area.' };
};

const scoreExperience = (job, profile) => {
  const min = Number(job.min_experience_years ?? job.experience_required ?? job.min_years_experience ?? 0);
  const max = Number(job.max_experience_years ?? 0);
  const have = profile.yearsOfExperience || 0;
  if (!min && !max) return { score: 75, note: 'No experience requirement listed.' };
  if (have >= min && (!max || have <= max + 3)) return { score: 100, note: `${have}+ yrs covers the ${min}+ yr requirement.` };
  if (have >= min * 0.7) return { score: 70, note: `${have} yrs — close to the ${min}+ yr requirement.` };
  return { score: 35, note: `Needs ${min}+ yrs; you have ${have}.` };
};

const scoreLocation = (job, profile) => {
  const remote = !!(job.is_remote || /remote/i.test(job.job_type || job.location || ''));
  if (remote && profile.openToRemote) return { score: 100, note: 'Remote — location-independent.' };
  const userLoc = profile.location || `${profile.city || ''} ${profile.country || ''}`;
  if (!job.location && !userLoc) return { score: 60, note: 'Location not specified.' };
  const sim = tokenSetSimilarity(job.location || '', userLoc);
  if (sim >= 0.5) return { score: 100, note: 'Location matches yours.' };
  if (profile.preferredLocations.some(l => tokenSetSimilarity(l, job.location || '') >= 0.5)) {
    return { score: 95, note: 'Matches one of your preferred locations.' };
  }
  if (profile.willingToRelocate) return { score: 70, note: 'Different location, but you indicated you\'d relocate.' };
  return { score: 30, note: 'Location doesn\'t match yours.' };
};

const scoreSalary = (job, profile) => {
  const userMin = profile.expectedSalaryMin;
  const userMax = profile.expectedSalaryMax;
  if (!userMin && !userMax) return { score: 70, note: 'Add expected salary for a better fit score.' };
  const jobMin = Number(job.salary_min ?? 0);
  const jobMax = Number(job.salary_max ?? 0);
  if (!jobMin && !jobMax) return { score: 60, note: 'Job salary not disclosed.' };
  const want = userMin || userMax;
  const offer = jobMax || jobMin;
  if (!want || !offer) return { score: 60, note: 'Partial salary info.' };
  if (offer >= want) return { score: 100, note: 'Meets or exceeds your expectations.' };
  const ratio = offer / want;
  if (ratio >= 0.9) return { score: 80, note: 'Slightly below your expectations.' };
  if (ratio >= 0.7) return { score: 50, note: 'Notably below your expectations.' };
  return { score: 20, note: 'Well below your expectations.' };
};

const computeMatch = (job, profile) => {
  const breakdown = {
    skills:     scoreSkills(job.skills, profile.skills),
    role:       scoreRole(job.position || job._raw?.title || '', profile),
    experience: scoreExperience(job._raw || {}, profile),
    location:   scoreLocation(job._raw || {}, profile),
    salary:     scoreSalary(job._raw || {}, profile),
  };
  let overall = 0;
  for (const [k, w] of Object.entries(MATCH_WEIGHTS)) {
    overall += (breakdown[k].score || 0) * w;
  }
  return {
    overall: Math.round(overall),
    breakdown,
  };
};

// Rule-based AI insights — used as fallback when /api/ai/career-advice fails
const buildRuleBasedInsights = (job, profile) => {
  const userSkillNames = new Set(profile.skills.map(s => normalize(s.name)));
  const matched = job.skills.filter(s => userSkillNames.has(normalize(s)));
  const missing = job.skills.filter(s => !userSkillNames.has(normalize(s)));

  const reasons = [];
  if (matched.length) reasons.push(`Skills overlap on ${matched.length} of ${job.skills.length}: ${matched.slice(0, 4).join(', ')}.`);
  if (profile.yearsOfExperience > 0) reasons.push(`${profile.yearsOfExperience}+ years of professional experience.`);
  if (profile.currentTitle) {
    const sim = tokenSetSimilarity(job.position, profile.currentTitle);
    if (sim > 0.3) reasons.push(`Current role "${profile.currentTitle}" aligns with this position.`);
  }
  if (profile.certifications.length) reasons.push(`${profile.certifications.length} relevant certification${profile.certifications.length === 1 ? '' : 's'} strengthen your profile.`);
  if (profile.projects.length) reasons.push(`${profile.projects.length} portfolio project${profile.projects.length === 1 ? '' : 's'} demonstrate practical work.`);
  if (!reasons.length) reasons.push('Your profile has been compared against the job requirements.');

  const suggestions = [];
  if (missing.length) suggestions.push(`Highlight transferable experience for missing skills: ${missing.slice(0, 3).join(', ')}.`);
  if (profile.projects.length) suggestions.push(`Reference your project "${profile.projects[0].name || profile.projects[0].title}" if relevant.`);
  if (!profile.summary || profile.summary.length < 100) suggestions.push('Expand your profile summary — recruiters use it as a first filter.');
  if (!profile.resumeUrl) suggestions.push('Upload a CV — it\'ll be attached automatically to Quick Apply.');
  suggestions.push('Tailor your application to mirror language from the job description.');

  const gap = job.skills.map(skill => {
    const have = profile.skills.find(s => normalize(s.name) === normalize(skill));
    return {
      skill,
      importance: missing.includes(skill) ? 'High' : 'Medium',
      userLevel:  have ? have.level : 'Not specified',
    };
  });

  return { matchReasons: reasons.slice(0, 5), suggestions: suggestions.slice(0, 5), gapAnalysis: gap };
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function AIJobMatchingScreen({ navigation }) {
  // STATE
  const [isLoading, setIsLoading]           = useState(true);
  const [isRefreshing, setIsRefreshing]     = useState(false);
  const [matchingJobs, setMatchingJobs]     = useState([]);
  const [savedJobIds, setSavedJobIds]       = useState(new Set());
  const [appliedJobIds, setAppliedJobIds]   = useState(new Set());
  const [activeFilter, setActiveFilter]     = useState('all');
  const [error, setError]                   = useState(null);
  const [apiOffline, setApiOffline]         = useState(false);
  const [expandedCardId, setExpandedCardId] = useState(null);

  const [showAIInsights, setShowAIInsights] = useState(false);
  const [selectedJob, setSelectedJob]       = useState(null);
  const [isAnalyzing, setIsAnalyzing]       = useState(false);
  const [aiInsights, setAiInsights]         = useState(null);

  const [searchQuery, setSearchQuery]         = useState('');
  const [showSkillsModal, setShowSkillsModal] = useState(false);
  const [profile, setProfile]                 = useState(normalizeProfile(null));
  const [newSkill, setNewSkill]               = useState('');
  const [selectedLevel, setSelectedLevel]     = useState('Beginner');

  // Title-lock: when ON, hide jobs whose role-similarity score is too low,
  // so users see jobs aligned with their actual job title. Auto-bypassed
  // when there's a search query (so users can still explore).
  const [titleLock, setTitleLock]             = useState(true);
  const TITLE_LOCK_THRESHOLD = 40; // role score must be at least this

  const isMounted = useRef(true);
  useEffect(() => { isMounted.current = true; return () => { isMounted.current = false; }; }, []);

  const filters = [
    { id: 'all',         name: 'All' },
    { id: 'best-match',  name: 'Best Match' },
    { id: 'engineering', name: 'Engineering' },
    { id: 'technology',  name: 'Technology' },
    { id: 'management',  name: 'Management' },
    { id: 'design',      name: 'Design' },
    { id: 'remote',      name: 'Remote' },
  ];

  // ───────────────────────────────────────────────────────────────────────
  // PROFILE LOADING
  // ───────────────────────────────────────────────────────────────────────
  const loadProfile = useCallback(async () => {
    try {
      await ProfileManagementService.bootstrap();
      const r = await ProfileManagementService.getProfile();
      if (r?.success && r?.data) {
        const np = normalizeProfile(r.data);
        if (isMounted.current) setProfile(np);
        return np;
      }
    } catch (e) {
      console.warn('Profile hydration failed:', e?.message);
    }
    return normalizeProfile(null);
  }, []);

  const persistProfile = async (updates) => {
    try {
      await ProfileManagementService.updateProfile(updates);
    } catch (e) { console.warn('Profile save failed:', e?.message); }
  };

  // ───────────────────────────────────────────────────────────────────────
  // JOB FETCH
  // ───────────────────────────────────────────────────────────────────────
  const fetchJobs = useCallback(async ({ silent = false, profileOverride = null } = {}) => {
    if (!silent) setIsLoading(true);
    setError(null);
    setApiOffline(false);

    try {
      const useProfile = profileOverride || profile;

      // Title-lock: pre-filter the API call by a distinctive keyword from the
      // user's current title (or most recent experience). If the filtered
      // result set is small, we also pull the unfiltered list and merge —
      // local scoring will float the right ones to the top.
      const lockTitle =
        useProfile.currentTitle ||
        useProfile.experience?.[0]?.title ||
        useProfile.experience?.[0]?.position ||
        '';
      const keyword = titleLock ? extractTitleKeyword(lockTitle) : null;

      let jobsRaw = null;
      if (keyword) {
        const filteredResp = await apiFetch(
          `/api/data/jobs?is_active=true&title=${encodeURIComponent(keyword)}&sort=created_at&order=desc&limit=50`
        );
        const arr = Array.isArray(filteredResp) ? filteredResp
          : Array.isArray(filteredResp?.data) ? filteredResp.data
          : Array.isArray(filteredResp?.results) ? filteredResp.results
          : null;
        if (arr && arr.length >= 8) {
          jobsRaw = arr;
        } else {
          // Too few results from the keyword filter — fall back to a wider
          // pull so the user isn't left with an almost-empty screen.
          const wideResp = await apiFetch(
            '/api/data/jobs?is_active=true&sort=created_at&order=desc&limit=50'
          );
          const wideArr = Array.isArray(wideResp) ? wideResp
            : Array.isArray(wideResp?.data) ? wideResp.data
            : Array.isArray(wideResp?.results) ? wideResp.results
            : [];
          // Merge, dedup by id, keyword-matched first
          const seen = new Set();
          jobsRaw = [...(arr || []), ...wideArr].filter(j => {
            if (!j?.id) return false;
            const id = String(j.id);
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
          });
        }
      } else {
        const resp = await apiFetch(
          '/api/data/jobs?is_active=true&sort=created_at&order=desc&limit=50'
        );
        jobsRaw = Array.isArray(resp) ? resp
          : Array.isArray(resp?.data) ? resp.data
          : Array.isArray(resp?.results) ? resp.results
          : null;
      }

      if (!jobsRaw) throw new Error('Unexpected response');

      // Resolve employer names + logos
      const companyIds = [...new Set(
        jobsRaw
          .filter(j => j?.company_id && !(j.company_name || j.employer_name))
          .map(j => j.company_id)
      )];
      const companyMap = {};
      if (companyIds.length) {
        const settled = await Promise.allSettled(
          companyIds.map(id =>
            apiFetch(`/api/data/company_profiles?id=${id}&limit=1`).then(res => {
              const row = Array.isArray(res) ? res[0]
                       : Array.isArray(res?.data) ? res.data[0]
                       : (res && typeof res === 'object' && !res.error) ? res : null;
              return { id, row };
            })
          )
        );
        settled.forEach(p => {
          if (p.status === 'fulfilled' && p.value?.row) companyMap[p.value.id] = p.value.row;
        });
      }

      // Pull saved + applied — prefer email-scoped endpoint when present
      let savedIds = new Set(), appliedIds = new Set();
      try {
        const s = await apiFetch('/api/data/saved_jobs?sort=created_at&order=desc&limit=200');
        const arr = Array.isArray(s) ? s : Array.isArray(s?.data) ? s.data : [];
        savedIds = new Set(arr.map(x => String(x.job_id)).filter(Boolean));
      } catch {}
      try {
        // Try the job-seeker scoped endpoint first; fall back to generic
        let a = await apiFetch('/api/professional/my-applications').catch(() => null);
        if (!a || a?.error) {
          a = await apiFetch(`/api/data/applications?candidate_email=${encodeURIComponent(useProfile.email || '')}&limit=200`);
        }
        const arr = Array.isArray(a) ? a : Array.isArray(a?.data) ? a.data : [];
        appliedIds = new Set(arr.map(x => String(x.job_id)).filter(Boolean));
      } catch {}

      const normalized = jobsRaw.map(j => normalizeJob(j, companyMap, useProfile, savedIds, appliedIds)).filter(Boolean);

      if (!isMounted.current) return;
      setMatchingJobs(normalized);
      setSavedJobIds(savedIds);
      setAppliedJobIds(appliedIds);
      console.log(`✅ AI Matching: ${normalized.length} jobs, profile @ ${computeProfileCompleteness(useProfile)}%`);
    } catch (err) {
      console.error('❌ Job fetch failed:', err);
      if (!isMounted.current) return;
      setApiOffline(true);
      setError(err?.message || 'Could not load jobs');
    } finally {
      if (isMounted.current && !silent) setIsLoading(false);
    }
  }, [profile, titleLock]);

  const normalizeJob = (j, companyMap, prof, savedIds, appliedIds) => {
    if (!j) return null;
    const company = j.company_id ? companyMap[j.company_id] : null;
    const employerName = j.company_name || j.employer_name || company?.company_name || company?.name || 'Unknown Employer';
    const employerLogo = j.company_logo || j.logo_url || j.logo || company?.logo || company?.logo_url || null;
    const location =
      j.location || j.job_location ||
      [j.city, j.state, j.country].filter(Boolean).join(', ') ||
      [company?.city, company?.country].filter(Boolean).join(', ') ||
      'Not specified';
    const jobType = j.job_type || j.employment_type || j.contract_type || 'Full-time';
    const skills = extractSkills(j);
    const createdAt = j.created_at || j.posted_at || j.published_at || null;

    const base = {
      id: String(j.id),
      position:  j.title || j.position || j.job_title || 'Job Opening',
      company:   employerName,
      companyId: j.company_id || null,
      location,
      salary:    formatSalary(j),
      logo:      employerLogo && String(employerLogo).startsWith('http') ? employerLogo : null,
      postedDate: formatRelativeDate(createdAt),
      jobType,
      isRemote: !!(j.is_remote || /remote/i.test(jobType) || /remote/i.test(location)),
      skills,
      isNew:    createdAt ? (Date.now() - new Date(createdAt).getTime()) < 1000 * 60 * 60 * 24 * 7 : false,
      category: inferCategory(j.title || j.position || '', j.department || j.category),
      description: j.description || j.job_description || '',
      isSaved:   savedIds.has(String(j.id)),
      isApplied: appliedIds.has(String(j.id)),
      _raw: j,
    };

    const match = computeMatch(base, prof);
    base.matchPercentage = match.overall;
    base.matchBreakdown  = match.breakdown;
    return base;
  };

  // ───────────────────────────────────────────────────────────────────────
  // INITIAL LOAD
  // ───────────────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      const p = await loadProfile();
      await fetchJobs({ profileOverride: p });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-score when profile changes
  useEffect(() => {
    if (matchingJobs.length === 0) return;
    setMatchingJobs(prev => prev.map(j => {
      const m = computeMatch(j, profile);
      return { ...j, matchPercentage: m.overall, matchBreakdown: m.breakdown };
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  // Refetch when title-lock toggles (so API keyword filter updates).
  // Skip the very first run — initial load already fetches.
  const titleLockInitialized = useRef(false);
  useEffect(() => {
    if (!titleLockInitialized.current) {
      titleLockInitialized.current = true;
      return;
    }
    fetchJobs({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [titleLock]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const p = await loadProfile();
      await fetchJobs({ silent: true, profileOverride: p });
    } finally {
      if (isMounted.current) setIsRefreshing(false);
    }
  };

  // ───────────────────────────────────────────────────────────────────────
  // SAVE / APPLY / INSIGHTS
  // ───────────────────────────────────────────────────────────────────────
  const toggleSaveJob = async (job) => {
    const wasSaved = job.isSaved;
    setMatchingJobs(prev => prev.map(j => j.id === job.id ? { ...j, isSaved: !wasSaved } : j));
    try {
      if (wasSaved) {
        await apiFetch(`/api/data/saved_jobs/${job.id}`, { method: 'DELETE' });
      } else {
        await apiFetch('/api/data/saved_jobs', {
          method: 'POST',
          body: JSON.stringify({ job_id: Number(job.id) }),
        });
      }
    } catch (e) {
      setMatchingJobs(prev => prev.map(j => j.id === job.id ? { ...j, isSaved: wasSaved } : j));
      Alert.alert('Save failed', e?.message || 'Could not update saved jobs.');
    }
  };

  // Build a rich apply payload using the FULL profile — no half-filled apps
  const buildApplyPayload = (job) => ({
    job_id:             Number(job.id),
    company_id:         job.companyId,
    candidate_name:     profile.name,
    candidate_email:    profile.email,
    candidate_phone:    profile.phone,
    candidate_location: profile.location,
    current_company:    profile.currentCompany,
    current_title:      profile.currentTitle,
    experience_years:   profile.yearsOfExperience,
    education: profile.education.length
      ? profile.education.map(e => `${e.degree || ''} ${e.field_of_study || ''} – ${e.institution || ''}`.trim()).join(' | ')
      : '',
    skills:         profile.skills.map(s => s.name),
    languages:      profile.languages.map(l => typeof l === 'string' ? l : l.name).filter(Boolean),
    certifications: profile.certifications.map(c => c.name || c.title).filter(Boolean),
    resume_url:     profile.resumeUrl,
    portfolio_url:  profile.portfolioUrl,
    linkedin_url:   profile.linkedinUrl,
    github_url:     profile.githubUrl,
    website_url:    profile.websiteUrl,
    source:         'mobile_ai_matching',
    ai_score:       job.matchPercentage,
  });

  const quickApply = async (job) => {
    if (job.isApplied) {
      Alert.alert('Already applied', `You've already applied to ${job.position} at ${job.company}.`);
      return;
    }
    // Block apply if profile is too thin — better UX than a silent bad submission
    const completeness = computeProfileCompleteness(profile);
    if (completeness < 40) {
      Alert.alert(
        'Profile incomplete',
        `Your profile is only ${completeness}% complete. Recruiters typically reject applications with sparse profiles. Finish your profile first?`,
        [
          { text: 'Apply anyway', style: 'destructive', onPress: () => doApply(job) },
          { text: 'Complete profile', onPress: () => navigation.navigate('Profile') },
        ],
      );
      return;
    }
    Alert.alert(
      'Quick Apply',
      `Submit application to ${job.position} at ${job.company} using your full profile?`,
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Apply', onPress: () => doApply(job) },
      ],
    );
  };

  const doApply = async (job) => {
    try {
      const payload = buildApplyPayload(job);
      const r = await apiFetch('/api/recruitment/apply', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (r?.error) throw new Error(r.error);
      setMatchingJobs(prev => prev.map(j => j.id === job.id ? { ...j, isApplied: true } : j));
      setAppliedJobIds(prev => new Set([...prev, job.id]));
      Alert.alert('Application sent', `Your application to ${job.company} has been submitted with your full profile.`);
    } catch (e) {
      // Fall back to full apply screen with pre-filled context
      navigation.navigate('JobApplication', {
        jobId: job.id,
        job: { id: job.id, title: job.position, company: job.company, location: job.location, salary: job.salary, jobType: job.jobType },
        prefill: buildApplyPayload(job),
      });
    }
  };

  const showInsightsForJob = async (job) => {
    setSelectedJob(job);
    setAiInsights(null);
    setIsAnalyzing(true);
    setShowAIInsights(true);

    const ruleBased = buildRuleBasedInsights(job, profile);

    try {
      const prompt = [
        'You are an AI career coach. Analyse this job-to-candidate match and respond with valid JSON only — no prose, no markdown.',
        '',
        'CANDIDATE PROFILE:',
        `Name: ${profile.name}`,
        `Current role: ${profile.currentTitle || 'Not specified'} at ${profile.currentCompany || 'N/A'}`,
        `Experience: ${profile.yearsOfExperience} years`,
        `Location: ${profile.location} ${profile.willingToRelocate ? '(willing to relocate)' : ''} ${profile.openToRemote ? '(open to remote)' : ''}`,
        `Summary: ${(profile.summary || '').slice(0, 400)}`,
        '',
        'SKILLS:',
        profile.skills.map(s => `- ${s.name} (${s.level})`).join('\n') || '- (none listed)',
        '',
        'EXPERIENCE:',
        profile.experience.slice(0, 5).map(e =>
          `- ${e.title || e.position} at ${e.company || ''} (${e.start_date || ''} → ${e.end_date || (e.current ? 'present' : '')}): ${(e.description || '').slice(0, 200)}`
        ).join('\n') || '- (none listed)',
        '',
        'EDUCATION:',
        profile.education.slice(0, 3).map(e => `- ${e.degree || ''} ${e.field_of_study || ''} – ${e.institution || ''}`).join('\n') || '- (none listed)',
        '',
        'CERTIFICATIONS:',
        profile.certifications.slice(0, 5).map(c => `- ${c.name || c.title}${c.issuing_organization ? ` (${c.issuing_organization})` : ''}`).join('\n') || '- (none listed)',
        '',
        'PROJECTS:',
        profile.projects.slice(0, 5).map(p => `- ${p.name || p.title}: ${(p.description || '').slice(0, 150)}`).join('\n') || '- (none listed)',
        '',
        'JOB:',
        `Title: ${job.position}`,
        `Company: ${job.company}`,
        `Location: ${job.location}`,
        `Type: ${job.jobType}${job.isRemote ? ' (Remote)' : ''}`,
        `Salary: ${job.salary}`,
        `Required skills: ${job.skills.join(', ') || 'not specified'}`,
        job.description ? `Description: ${job.description.slice(0, 1000)}` : '',
        '',
        'MATCH BREAKDOWN (already computed):',
        `Skills ${job.matchBreakdown.skills.score}% · Role ${job.matchBreakdown.role.score}% · Experience ${job.matchBreakdown.experience.score}% · Location ${job.matchBreakdown.location.score}% · Salary ${job.matchBreakdown.salary.score}%`,
        '',
        'Respond with JSON shaped exactly like:',
        '{',
        '  "matchReasons":  [string, string, string, string],',
        '  "suggestions":   [string, string, string, string],',
        '  "gapAnalysis":   [ { "skill": string, "importance": "High"|"Medium"|"Low", "userLevel": string } ]',
        '}',
        'Reference specific items from the candidate\'s experience, projects, or certs in matchReasons. Be concrete.',
      ].join('\n');

      const r = await apiFetch('/api/ai', {
        method: 'POST',
        body: JSON.stringify({ prompt }),
      });

      let parsed = null;
      if (r && typeof r === 'object' && r.matchReasons) parsed = r;
      else if (typeof r?.response === 'string' || typeof r?.text === 'string' || typeof r?.content === 'string') {
        const text = r.response || r.text || r.content;
        const start = text.indexOf('{'), end = text.lastIndexOf('}');
        if (start >= 0 && end > start) {
          try { parsed = JSON.parse(text.slice(start, end + 1)); } catch {}
        }
      }

      if (parsed && Array.isArray(parsed.matchReasons) && Array.isArray(parsed.suggestions) && Array.isArray(parsed.gapAnalysis)) {
        if (isMounted.current) setAiInsights(parsed);
      } else if (isMounted.current) {
        setAiInsights(ruleBased);
      }
    } catch (e) {
      console.warn('AI insights call failed, using rule-based fallback:', e?.message);
      if (isMounted.current) setAiInsights(ruleBased);
    } finally {
      if (isMounted.current) setIsAnalyzing(false);
    }
  };

  // ───────────────────────────────────────────────────────────────────────
  // SKILL ADD / REMOVE (kept on profile)
  // ───────────────────────────────────────────────────────────────────────
  const addSkill = async () => {
    const name = newSkill.trim();
    if (!name) return;
    if (profile.skills.some(s => normalize(s.name) === normalize(name))) {
      Alert.alert('Already added', `"${name}" is already in your skills.`);
      return;
    }
    const nextSkills = [...profile.skills, { id: `${Date.now()}`, name, level: selectedLevel }];
    setProfile(p => ({ ...p, skills: nextSkills }));
    setNewSkill('');
    persistProfile({ skills: nextSkills });
  };

  const removeSkill = async (skillId) => {
    const nextSkills = profile.skills.filter(s => s.id !== skillId);
    setProfile(p => ({ ...p, skills: nextSkills }));
    persistProfile({ skills: nextSkills });
  };

  // ───────────────────────────────────────────────────────────────────────
  // FILTERED LIST
  // ───────────────────────────────────────────────────────────────────────
  const filteredJobs = useMemo(() => {
    let out = matchingJobs;

    // Title-lock — hide jobs that don't align with the user's current job
    // title. Auto-bypass when there's a search query (so users can still
    // explore outside their lane). Also bypass if the user has no title set
    // or if they explicitly turned the lock off.
    const lockActive =
      titleLock &&
      !!profile.currentTitle &&
      !searchQuery.trim();

    if (lockActive) {
      out = out.filter(j => (j.matchBreakdown?.role?.score ?? 0) >= TITLE_LOCK_THRESHOLD);
    }

    if (activeFilter === 'best-match') out = out.filter(j => j.matchPercentage >= 70);
    else if (activeFilter === 'remote') out = out.filter(j => j.isRemote);
    else if (activeFilter !== 'all') out = out.filter(j => j.category === activeFilter);

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      out = out.filter(j =>
        (j.position || '').toLowerCase().includes(q) ||
        (j.company  || '').toLowerCase().includes(q) ||
        (j.location || '').toLowerCase().includes(q) ||
        j.skills.some(sk => sk.toLowerCase().includes(q))
      );
    }
    return [...out].sort((a, b) => b.matchPercentage - a.matchPercentage);
  }, [matchingJobs, activeFilter, searchQuery, titleLock, profile.currentTitle]);

  // ───────────────────────────────────────────────────────────────────────
  // PROFILE COMPLETENESS
  // ───────────────────────────────────────────────────────────────────────
  const completeness = useMemo(() => computeProfileCompleteness(profile), [profile]);

  // ───────────────────────────────────────────────────────────────────────
  // RENDER
  // ───────────────────────────────────────────────────────────────────────
  const getMatchColor = (pct) => {
    if (pct >= 85) return '#16a34a';
    if (pct >= 70) return '#0e4e96';
    if (pct >= 55) return '#f59e0b';
    return '#64748B';
  };

  const renderProfileStrip = () => (
    <View style={styles.profileStrip}>
      {profile.photo ? (
        <Image source={{ uri: profile.photo }} style={styles.profileAvatar} />
      ) : (
        <View style={[styles.profileAvatar, styles.profileAvatarFallback]}>
          <Text style={styles.profileAvatarInitial}>{(profile.name || 'U').charAt(0).toUpperCase()}</Text>
        </View>
      )}
      <View style={styles.profileStripContent}>
        <Text style={styles.profileStripName} numberOfLines={1}>{profile.name || 'Your Profile'}</Text>
        <Text style={styles.profileStripTitle} numberOfLines={1}>
          {profile.currentTitle ? `${profile.currentTitle}${profile.currentCompany ? ` · ${profile.currentCompany}` : ''}` : 'Add your current role'}
        </Text>
        <View style={styles.profileStripMeta}>
          <Text style={styles.profileStripStat}>{profile.skills.length} skills</Text>
          <Text style={styles.profileStripDot}>·</Text>
          <Text style={styles.profileStripStat}>{profile.yearsOfExperience} yrs exp</Text>
          <Text style={styles.profileStripDot}>·</Text>
          <Text style={styles.profileStripStat}>{profile.projects.length} projects</Text>
          <Text style={styles.profileStripDot}>·</Text>
          <Text style={styles.profileStripStat}>{profile.certifications.length} certs</Text>
        </View>
      </View>
      <TouchableOpacity style={styles.profileCompletenessBadge} onPress={() => setShowSkillsModal(true)}>
        <Text style={[styles.profileCompletenessText, { color: completeness >= 70 ? '#16a34a' : completeness >= 40 ? '#f59e0b' : '#dc2626' }]}>
          {completeness}%
        </Text>
        <Text style={styles.profileCompletenessLabel}>complete</Text>
      </TouchableOpacity>
    </View>
  );

  const renderFilterItem = ({ item }) => (
    <TouchableOpacity
      style={[styles.filterItem, activeFilter === item.id && styles.activeFilterItem]}
      onPress={() => setActiveFilter(item.id)}
    >
      <Text style={[styles.filterText, activeFilter === item.id && styles.activeFilterText]}>{item.name}</Text>
    </TouchableOpacity>
  );

  const renderBreakdownBar = (label, score, note) => (
    <View key={label} style={styles.breakdownRow}>
      <Text style={styles.breakdownLabel}>{label}</Text>
      <View style={styles.breakdownBarBg}>
        <View style={[styles.breakdownBarFill, {
          width: `${Math.max(score, 5)}%`,
          backgroundColor: getMatchColor(score),
        }]} />
      </View>
      <Text style={[styles.breakdownScore, { color: getMatchColor(score) }]}>{score}%</Text>
    </View>
  );

  const renderJobItem = ({ item }) => {
    const expanded = expandedCardId === item.id;
    return (
      <View style={styles.jobCard}>
        <TouchableOpacity
          onPress={() => navigation.navigate('JobDetails', { jobId: item.id })}
          activeOpacity={0.85}
        >
          <View style={styles.jobCardHeader}>
            <Image
              source={item.logo ? { uri: item.logo } : require('../../../assets/default-company-logo.png')}
              style={styles.companyLogo}
              resizeMode="contain"
              defaultSource={require('../../../assets/default-company-logo.png')}
            />
            <View style={styles.jobInfo}>
              <Text style={styles.positionTitle} numberOfLines={2}>{item.position}</Text>
              <Text style={styles.companyName} numberOfLines={1}>{item.company}</Text>
              <Text style={styles.locationText} numberOfLines={1}>
                {item.isRemote ? '🌐 Remote · ' : ''}{item.location}
              </Text>
            </View>
            <View style={[styles.matchBadge, { backgroundColor: getMatchColor(item.matchPercentage) + '20' }]}>
              <Text style={[styles.matchText, { color: getMatchColor(item.matchPercentage) }]}>
                {item.matchPercentage}%
              </Text>
              <Text style={[styles.matchSubtext, { color: getMatchColor(item.matchPercentage) }]}>Match</Text>
            </View>
          </View>

          <View style={styles.jobDetails}>
            <View style={styles.jobDetailItem}>
              <Ionicons name="cash-outline" size={14} color="#64748B" />
              <Text style={styles.jobDetailText} numberOfLines={1}>{item.salary}</Text>
            </View>
            <View style={styles.jobDetailItem}>
              <Ionicons name="time-outline" size={14} color="#64748B" />
              <Text style={styles.jobDetailText}>{item.postedDate}</Text>
            </View>
            <View style={styles.jobDetailItem}>
              <Ionicons name="briefcase-outline" size={14} color="#64748B" />
              <Text style={styles.jobDetailText}>{item.jobType}</Text>
            </View>
          </View>
        </TouchableOpacity>

        {/* Match breakdown — expandable */}
        <TouchableOpacity
          style={styles.breakdownToggle}
          onPress={() => setExpandedCardId(expanded ? null : item.id)}
        >
          <Ionicons name={expanded ? 'chevron-up' : 'chevron-down'} size={14} color="#0e4e96" />
          <Text style={styles.breakdownToggleText}>
            {expanded ? 'Hide breakdown' : 'Why this match?'}
          </Text>
        </TouchableOpacity>

        {expanded && (
          <View style={styles.breakdownContainer}>
            {renderBreakdownBar('Skills',     item.matchBreakdown.skills.score,     item.matchBreakdown.skills.note)}
            {renderBreakdownBar('Role',       item.matchBreakdown.role.score,       item.matchBreakdown.role.note)}
            {renderBreakdownBar('Experience', item.matchBreakdown.experience.score, item.matchBreakdown.experience.note)}
            {renderBreakdownBar('Location',   item.matchBreakdown.location.score,   item.matchBreakdown.location.note)}
            {renderBreakdownBar('Salary',     item.matchBreakdown.salary.score,     item.matchBreakdown.salary.note)}
            <View style={styles.breakdownNotes}>
              {Object.entries(item.matchBreakdown).map(([k, v]) => v.note ? (
                <Text key={k} style={styles.breakdownNote}>• {v.note}</Text>
              ) : null)}
            </View>
          </View>
        )}

        {item.skills.length > 0 && (
          <View style={styles.skillsContainer}>
            {item.skills.slice(0, 8).map((skill, idx) => {
              const has = profile.skills.some(s => normalize(s.name) === normalize(skill));
              return (
                <View key={idx} style={[styles.skillBadge, has && styles.matchedSkillBadge]}>
                  {has && <Ionicons name="checkmark-circle" size={11} color="#16a34a" style={styles.skillCheckmark} />}
                  <Text style={[styles.skillText, has && styles.matchedSkillText]}>{skill}</Text>
                </View>
              );
            })}
            {item.skills.length > 8 && (
              <View style={styles.skillBadge}><Text style={styles.skillText}>+{item.skills.length - 8}</Text></View>
            )}
          </View>
        )}

        <View style={styles.jobActions}>
          <TouchableOpacity style={styles.actionButton} onPress={() => showInsightsForJob(item)}>
            <Ionicons name="bulb-outline" size={14} color="#0e4e96" style={styles.actionIcon} />
            <Text style={styles.actionButtonText}>AI Insights</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.actionButton} onPress={() => toggleSaveJob(item)}>
            <Ionicons
              name={item.isSaved ? 'bookmark' : 'bookmark-outline'}
              size={14}
              color={item.isSaved ? '#f59e0b' : '#64748B'}
              style={styles.actionIcon}
            />
            <Text style={styles.actionButtonText}>{item.isSaved ? 'Saved' : 'Save'}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.actionButton, styles.applyButton, item.isApplied && { backgroundColor: '#16a34a' }]}
            onPress={() => quickApply(item)}
            disabled={item.isApplied}
          >
            <Text style={styles.applyButtonText}>{item.isApplied ? 'Applied ✓' : 'Apply Now'}</Text>
          </TouchableOpacity>
        </View>

        {item.isNew && (
          <View style={styles.newBadge}><Text style={styles.newBadgeText}>NEW</Text></View>
        )}
      </View>
    );
  };

  const renderEmptyState = () => (
    <View style={styles.emptyContainer}>
      <Ionicons name="briefcase-outline" size={64} color="#E2E8F0" />
      <Text style={styles.emptyTitle}>No Matching Jobs</Text>
      <Text style={styles.emptyText}>
        {searchQuery
          ? `No matches for "${searchQuery}". Try a broader search.`
          : 'No jobs in this filter. Try a different filter or update your profile to widen matches.'}
      </Text>
      <TouchableOpacity style={styles.updateProfileButton} onPress={() => setShowSkillsModal(true)}>
        <Text style={styles.updateProfileButtonText}>Update Skills</Text>
      </TouchableOpacity>
    </View>
  );

  // ───────────────────────────────────────────────────────────────────────
  // MODALS
  // ───────────────────────────────────────────────────────────────────────
  const renderAIInsightsModal = () => (
    <Modal visible={showAIInsights} animationType="slide" transparent onRequestClose={() => setShowAIInsights(false)}>
      <View style={styles.modalOverlay}>
        <View style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>AI Match Insights</Text>
            <TouchableOpacity style={styles.closeButton} onPress={() => setShowAIInsights(false)}>
              <Ionicons name="close" size={24} color="#64748B" />
            </TouchableOpacity>
          </View>

          {isAnalyzing ? (
            <View style={styles.analyzingContainer}>
              <ActivityIndicator size={36} color="#0e4e96" />
              <Text style={styles.analyzingText}>AI is reviewing your full profile vs this job...</Text>
            </View>
          ) : (
            <ScrollView style={styles.modalContent}>
              {selectedJob && (
                <>
                  <View style={styles.insightHeader}>
                    <Image
                      source={selectedJob.logo ? { uri: selectedJob.logo } : require('../../../assets/default-company-logo.png')}
                      style={styles.insightLogo}
                      resizeMode="contain"
                      defaultSource={require('../../../assets/default-company-logo.png')}
                    />
                    <View style={styles.insightHeaderInfo}>
                      <Text style={styles.insightPosition}>{selectedJob.position}</Text>
                      <Text style={styles.insightCompany}>{selectedJob.company}</Text>
                      <View style={[styles.insightMatchBadge, { backgroundColor: getMatchColor(selectedJob.matchPercentage) + '20' }]}>
                        <Text style={[styles.insightMatchText, { color: getMatchColor(selectedJob.matchPercentage) }]}>
                          {selectedJob.matchPercentage}% Match
                        </Text>
                      </View>
                    </View>
                  </View>

                  {/* Multi-factor breakdown in modal too */}
                  <View style={styles.insightSection}>
                    <Text style={styles.insightSectionTitle}>Match Breakdown</Text>
                    {renderBreakdownBar('Skills',     selectedJob.matchBreakdown.skills.score,     selectedJob.matchBreakdown.skills.note)}
                    {renderBreakdownBar('Role',       selectedJob.matchBreakdown.role.score,       selectedJob.matchBreakdown.role.note)}
                    {renderBreakdownBar('Experience', selectedJob.matchBreakdown.experience.score, selectedJob.matchBreakdown.experience.note)}
                    {renderBreakdownBar('Location',   selectedJob.matchBreakdown.location.score,   selectedJob.matchBreakdown.location.note)}
                    {renderBreakdownBar('Salary',     selectedJob.matchBreakdown.salary.score,     selectedJob.matchBreakdown.salary.note)}
                  </View>

                  <View style={styles.insightSection}>
                    <Text style={styles.insightSectionTitle}>Why You Match</Text>
                    {(aiInsights?.matchReasons || []).map((reason, i) => (
                      <View key={i} style={styles.insightItem}>
                        <Ionicons name="checkmark-circle" size={16} color="#16a34a" />
                        <Text style={styles.insightItemText}>{reason}</Text>
                      </View>
                    ))}
                  </View>

                  <View style={styles.insightSection}>
                    <Text style={styles.insightSectionTitle}>AI Suggestions</Text>
                    {(aiInsights?.suggestions || []).map((s, i) => (
                      <View key={i} style={styles.insightItem}>
                        <Ionicons name="bulb-outline" size={16} color="#0e4e96" />
                        <Text style={styles.insightItemText}>{s}</Text>
                      </View>
                    ))}
                  </View>

                  <View style={styles.insightSection}>
                    <Text style={styles.insightSectionTitle}>Skill Gap Analysis</Text>
                    <View style={styles.gapAnalysisHeader}>
                      <Text style={[styles.gapAnalysisHeaderText, styles.gapAnalysisSkill]}>Skill</Text>
                      <Text style={[styles.gapAnalysisHeaderText, styles.gapAnalysisImportance]}>Importance</Text>
                      <Text style={[styles.gapAnalysisHeaderText, styles.gapAnalysisStatus]}>Your Status</Text>
                    </View>
                    {(aiInsights?.gapAnalysis || []).map((gap, i) => (
                      <View key={i} style={styles.gapAnalysisRow}>
                        <Text style={styles.gapAnalysisSkill} numberOfLines={1}>{gap.skill}</Text>
                        <View style={[
                          styles.importanceBadge,
                          gap.importance === 'High' ? styles.highImportance :
                          gap.importance === 'Medium' ? styles.mediumImportance : styles.lowImportance,
                        ]}>
                          <Text style={styles.importanceText}>{gap.importance}</Text>
                        </View>
                        <Text style={[
                          styles.gapAnalysisStatus,
                          gap.userLevel === 'Not specified' ? styles.missingSkill :
                          String(gap.userLevel).toLowerCase().startsWith('similar') ? styles.similarSkill :
                          styles.existingSkill,
                        ]} numberOfLines={1}>
                          {gap.userLevel}
                        </Text>
                      </View>
                    ))}
                  </View>

                  <TouchableOpacity
                    style={styles.addMissingSkillsButton}
                    onPress={() => { setShowAIInsights(false); setShowSkillsModal(true); }}
                  >
                    <Text style={styles.addMissingSkillsText}>Update My Profile</Text>
                  </TouchableOpacity>
                </>
              )}
            </ScrollView>
          )}
        </View>
      </View>
    </Modal>
  );

  const renderSkillsModal = () => (
    <Modal visible={showSkillsModal} animationType="slide" transparent onRequestClose={() => setShowSkillsModal(false)}>
      <View style={styles.modalOverlay}>
        <View style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Profile & Skills</Text>
            <TouchableOpacity style={styles.closeButton} onPress={() => setShowSkillsModal(false)}>
              <Ionicons name="close" size={24} color="#64748B" />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.modalContent}>
            {/* Profile completeness summary */}
            <View style={styles.completenessCard}>
              <View style={styles.completenessHeader}>
                <Text style={styles.completenessTitle}>Profile Completeness</Text>
                <Text style={[styles.completenessPct, { color: completeness >= 70 ? '#16a34a' : completeness >= 40 ? '#f59e0b' : '#dc2626' }]}>
                  {completeness}%
                </Text>
              </View>
              <View style={styles.completenessBarBg}>
                <View style={[styles.completenessBarFill, {
                  width: `${completeness}%`,
                  backgroundColor: completeness >= 70 ? '#16a34a' : completeness >= 40 ? '#f59e0b' : '#dc2626',
                }]} />
              </View>
              <TouchableOpacity
                style={styles.editProfileLink}
                onPress={() => { setShowSkillsModal(false); navigation.navigate('Profile'); }}
              >
                <Text style={styles.editProfileLinkText}>Open full profile editor →</Text>
              </TouchableOpacity>
            </View>

            {/* Portfolio links */}
            <Text style={styles.currentSkillsTitle}>Portfolio Links</Text>
            <View style={styles.portfolioGrid}>
              <PortfolioLink icon="logo-linkedin" label="LinkedIn"   url={profile.linkedinUrl}  />
              <PortfolioLink icon="logo-github"   label="GitHub"     url={profile.githubUrl}    />
              <PortfolioLink icon="globe-outline" label="Portfolio"  url={profile.portfolioUrl} />
              <PortfolioLink icon="document-text-outline" label="Resume" url={profile.resumeUrl} />
            </View>

            {/* Add skill */}
            <Text style={styles.currentSkillsTitle}>Add a Skill</Text>
            <View style={styles.addSkillContainer}>
              <TextInput
                style={styles.skillInput}
                placeholder="e.g. React Native, Mechanical Engineering..."
                value={newSkill}
                onChangeText={setNewSkill}
                returnKeyType="done"
                onSubmitEditing={addSkill}
              />
              <View style={styles.skillLevelContainer}>
                <Text style={styles.skillLevelLabel}>Proficiency:</Text>
                <View style={styles.skillLevelOptions}>
                  {SKILL_LEVELS.map(level => (
                    <TouchableOpacity
                      key={level}
                      style={[styles.skillLevelOption, selectedLevel === level && styles.selectedSkillLevel]}
                      onPress={() => setSelectedLevel(level)}
                    >
                      <Text style={[styles.skillLevelText, selectedLevel === level && styles.selectedSkillLevelText]}>{level}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
              <TouchableOpacity
                style={[styles.addSkillButton, !newSkill.trim() && styles.disabledButton]}
                onPress={addSkill}
                disabled={!newSkill.trim()}
              >
                <Text style={styles.addSkillButtonText}>Add Skill</Text>
              </TouchableOpacity>
            </View>

            {/* Current skills */}
            <Text style={styles.currentSkillsTitle}>Your Skills ({profile.skills.length})</Text>
            <View style={styles.currentSkillsList}>
              {profile.skills.length === 0 ? (
                <Text style={[styles.emptyText, { textAlign: 'left', margin: 0 }]}>
                  No skills yet. Add some above.
                </Text>
              ) : profile.skills.map(skill => (
                <View key={skill.id} style={styles.currentSkillItem}>
                  <View style={styles.currentSkillInfo}>
                    <Text style={styles.currentSkillName} numberOfLines={1}>{skill.name}</Text>
                    <View style={[
                      styles.skillLevelBadge,
                      skill.level === 'Expert' ? styles.expertBadge :
                      skill.level === 'Advanced' ? styles.advancedBadge :
                      skill.level === 'Intermediate' ? styles.intermediateBadge : styles.beginnerBadge,
                    ]}>
                      <Text style={styles.skillLevelBadgeText}>{skill.level}</Text>
                    </View>
                  </View>
                  <TouchableOpacity style={styles.removeSkillButton} onPress={() => removeSkill(skill.id)}>
                    <Ionicons name="close-circle" size={20} color="#64748B" />
                  </TouchableOpacity>
                </View>
              ))}
            </View>

            <TouchableOpacity
              style={styles.updateMatchingButton}
              onPress={async () => { setShowSkillsModal(false); await fetchJobs({ silent: false }); }}
            >
              <Text style={styles.updateMatchingButtonText}>Re-run AI Matching</Text>
            </TouchableOpacity>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );

  // ───────────────────────────────────────────────────────────────────────
  // ROOT
  // ───────────────────────────────────────────────────────────────────────
  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={24} color="#FFFFFF" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>AI Job Matching</Text>
        <TouchableOpacity style={styles.headerButton} onPress={() => setShowSkillsModal(true)}>
          <Ionicons name="person-outline" size={24} color="#FFFFFF" />
        </TouchableOpacity>
      </View>

      {renderProfileStrip()}

      {/* Title lock — anchor matches to user's job title */}
      {profile.currentTitle ? (
        titleLock ? (
          <View style={styles.titleLockBar}>
            <Ionicons name="lock-closed" size={14} color="#0e4e96" />
            <Text style={styles.titleLockText} numberOfLines={1}>
              Matching jobs for <Text style={styles.titleLockTitle}>"{profile.currentTitle}"</Text>
            </Text>
            <TouchableOpacity
              style={styles.titleLockToggle}
              onPress={() => setTitleLock(false)}
            >
              <Text style={styles.titleLockToggleText}>Unlock</Text>
              <Ionicons name="close-circle" size={14} color="#64748B" />
            </TouchableOpacity>
          </View>
        ) : (
          <TouchableOpacity style={styles.titleLockBarUnlocked} onPress={() => setTitleLock(true)}>
            <Ionicons name="lock-open-outline" size={14} color="#64748B" />
            <Text style={styles.titleLockTextUnlocked}>
              Showing all jobs — tap to lock to <Text style={{ fontWeight: '600', color: '#0e4e96' }}>"{profile.currentTitle}"</Text>
            </Text>
          </TouchableOpacity>
        )
      ) : null}

      {completeness < 50 && (
        <TouchableOpacity style={styles.completenessBanner} onPress={() => setShowSkillsModal(true)}>
          <Ionicons name="alert-circle" size={16} color="#fff" />
          <Text style={styles.completenessBannerText}>
            Your profile is {completeness}% complete — finish it for better matches & higher response rate.
          </Text>
          <Ionicons name="chevron-forward" size={16} color="#fff" />
        </TouchableOpacity>
      )}

      {apiOffline && (
        <View style={styles.offlineBanner}>
          <Ionicons name="cloud-offline-outline" size={16} color="#856404" />
          <Text style={styles.offlineText}>Couldn't reach server. Pull down to retry.</Text>
        </View>
      )}

      <View style={styles.searchContainer}>
        <Ionicons name="search" size={20} color="#64748B" style={styles.searchIcon} />
        <TextInput
          style={styles.searchInput}
          placeholder="Search title, company, skill, location..."
          value={searchQuery}
          onChangeText={setSearchQuery}
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity style={styles.clearButton} onPress={() => setSearchQuery('')}>
            <Ionicons name="close-circle" size={20} color="#64748B" />
          </TouchableOpacity>
        )}
      </View>

      <View style={styles.filtersContainer}>
        <FlatList
          data={filters}
          renderItem={renderFilterItem}
          keyExtractor={item => item.id}
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.filtersList}
        />
      </View>

      {isLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.loadingText}>Scoring jobs against your full profile...</Text>
        </View>
      ) : error && matchingJobs.length === 0 ? (
        <View style={styles.errorContainer}>
          <Ionicons name="alert-circle-outline" size={60} color="#dc2626" />
          <Text style={styles.errorTitle}>Couldn't Load Jobs</Text>
          <Text style={styles.errorDescription}>{error}</Text>
          <TouchableOpacity style={styles.retryButton} onPress={() => fetchJobs()}>
            <Text style={styles.retryButtonText}>Retry</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <FlatList
          data={filteredJobs}
          renderItem={renderJobItem}
          keyExtractor={item => item.id}
          contentContainerStyle={styles.jobsList}
          showsVerticalScrollIndicator={false}
          ListEmptyComponent={renderEmptyState}
          refreshControl={
            <RefreshControl refreshing={isRefreshing} onRefresh={handleRefresh} colors={['#0e4e96']} tintColor="#0e4e96" />
          }
        />
      )}

      {renderAIInsightsModal()}
      {renderSkillsModal()}
    </SafeAreaView>
  );
}

// Small portfolio link chip
function PortfolioLink({ icon, label, url }) {
  const has = !!url;
  return (
    <TouchableOpacity
      style={[styles.portfolioChip, !has && styles.portfolioChipMissing]}
      onPress={() => {
        if (has) Linking.openURL(url).catch(() => Alert.alert('Cannot open link'));
        else Alert.alert('Not set', `Add a ${label} link in your profile.`);
      }}
    >
      <Ionicons name={icon} size={18} color={has ? '#0e4e96' : '#94A3B8'} />
      <Text style={[styles.portfolioChipLabel, !has && { color: '#94A3B8' }]} numberOfLines={1}>
        {label}
      </Text>
      <Ionicons name={has ? 'checkmark-circle' : 'add-circle-outline'} size={14} color={has ? '#16a34a' : '#94A3B8'} />
    </TouchableOpacity>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#F8FAFC' },
  header: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 16, paddingVertical: 12, backgroundColor: '#0e4e96',
  },
  backButton: { padding: 8 },
  headerTitle: { fontSize: 18, fontWeight: '600', color: '#FFFFFF' },
  headerButton: { padding: 8 },

  // Profile strip
  profileStrip: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#FFFFFF', paddingHorizontal: 16, paddingVertical: 12,
    borderBottomWidth: 1, borderBottomColor: '#E2E8F0',
  },
  profileAvatar: { width: 48, height: 48, borderRadius: 24, backgroundColor: '#E2E8F0' },
  profileAvatarFallback: { justifyContent: 'center', alignItems: 'center', backgroundColor: '#0e4e96' },
  profileAvatarInitial: { color: '#fff', fontSize: 22, fontWeight: 'bold' },
  profileStripContent: { flex: 1, marginLeft: 12 },
  profileStripName: { fontSize: 15, fontWeight: '700', color: '#0f172a' },
  profileStripTitle: { fontSize: 13, color: '#475569', marginTop: 1 },
  profileStripMeta: { flexDirection: 'row', alignItems: 'center', marginTop: 4, flexWrap: 'wrap' },
  profileStripStat: { fontSize: 11, color: '#64748B' },
  profileStripDot: { fontSize: 11, color: '#CBD5E1', marginHorizontal: 4 },
  profileCompletenessBadge: {
    alignItems: 'center', justifyContent: 'center',
    paddingHorizontal: 12, paddingVertical: 8,
    backgroundColor: '#F8FAFC', borderRadius: 10, borderWidth: 1, borderColor: '#E2E8F0',
  },
  profileCompletenessText: { fontSize: 16, fontWeight: '700' },
  profileCompletenessLabel: { fontSize: 9, color: '#94A3B8' },

  // Completeness banner
  completenessBanner: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#dc2626', paddingHorizontal: 16, paddingVertical: 10,
  },
  completenessBannerText: { flex: 1, marginLeft: 8, color: '#fff', fontSize: 12, fontWeight: '500' },

  // Title lock chip
  titleLockBar: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#E0F2FE', paddingHorizontal: 16, paddingVertical: 8,
    borderBottomWidth: 1, borderBottomColor: '#BAE6FD',
  },
  titleLockText: { flex: 1, marginLeft: 8, fontSize: 12, color: '#0f172a' },
  titleLockTitle: { fontWeight: '700', color: '#0e4e96' },
  titleLockToggle: {
    flexDirection: 'row', alignItems: 'center',
    paddingHorizontal: 8, paddingVertical: 4,
    backgroundColor: '#fff', borderRadius: 12,
    borderWidth: 1, borderColor: '#CBD5E1',
  },
  titleLockToggleText: { fontSize: 11, color: '#64748B', marginRight: 4, fontWeight: '600' },
  titleLockBarUnlocked: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#F8FAFC', paddingHorizontal: 16, paddingVertical: 8,
    borderBottomWidth: 1, borderBottomColor: '#E2E8F0',
  },
  titleLockTextUnlocked: { flex: 1, marginLeft: 8, fontSize: 12, color: '#475569' },

  offlineBanner: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#fff3cd', paddingHorizontal: 16, paddingVertical: 8,
    borderBottomWidth: 1, borderBottomColor: '#ffeeba',
  },
  offlineText: { marginLeft: 8, color: '#856404', fontSize: 12 },

  searchContainer: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#FFFFFF', margin: 16, marginTop: 12, marginBottom: 8,
    paddingHorizontal: 12, borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0',
  },
  searchIcon: { marginRight: 8 },
  searchInput: { flex: 1, paddingVertical: 10, fontSize: 16 },
  clearButton: { padding: 4 },

  filtersContainer: { backgroundColor: '#FFFFFF', borderBottomWidth: 1, borderBottomColor: '#E2E8F0' },
  filtersList: { paddingHorizontal: 16, paddingVertical: 12 },
  filterItem: {
    paddingHorizontal: 14, paddingVertical: 7,
    borderRadius: 16, backgroundColor: '#F1F5F9', marginRight: 8,
  },
  activeFilterItem: { backgroundColor: '#0e4e96' },
  filterText: { fontSize: 13, fontWeight: '500', color: '#64748B' },
  activeFilterText: { color: '#FFFFFF' },

  jobsList: { padding: 16 },
  jobCard: {
    backgroundColor: '#FFFFFF', borderRadius: 12, padding: 16, marginBottom: 16,
    borderWidth: 1, borderColor: '#E2E8F0',
    shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.05, shadowRadius: 5,
    elevation: 2, position: 'relative',
  },
  jobCardHeader: { flexDirection: 'row', marginBottom: 12 },
  companyLogo: { width: 48, height: 48, borderRadius: 8, marginRight: 12, backgroundColor: '#F1F5F9' },
  jobInfo: { flex: 1 },
  positionTitle: { fontSize: 15, fontWeight: 'bold', color: '#0f172a', marginBottom: 2 },
  companyName: { fontSize: 13, color: '#334155', marginBottom: 2 },
  locationText: { fontSize: 11, color: '#64748B' },
  matchBadge: {
    paddingHorizontal: 10, paddingVertical: 6, borderRadius: 10,
    alignItems: 'center', justifyContent: 'center',
  },
  matchText: { fontSize: 16, fontWeight: '700' },
  matchSubtext: { fontSize: 9, fontWeight: '600', letterSpacing: 0.5 },

  jobDetails: {
    flexDirection: 'row', flexWrap: 'wrap', marginBottom: 8,
    borderBottomWidth: 1, borderBottomColor: '#E2E8F0', paddingBottom: 10,
  },
  jobDetailItem: { flexDirection: 'row', alignItems: 'center', marginRight: 12, marginBottom: 4 },
  jobDetailText: { fontSize: 11, color: '#64748B', marginLeft: 4, maxWidth: 200 },

  // Breakdown toggle + container
  breakdownToggle: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    paddingVertical: 6, marginBottom: 6,
  },
  breakdownToggleText: { fontSize: 12, color: '#0e4e96', fontWeight: '600', marginLeft: 4 },
  breakdownContainer: {
    backgroundColor: '#F8FAFC', borderRadius: 8, padding: 10, marginBottom: 12,
  },
  breakdownRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 6 },
  breakdownLabel: { width: 72, fontSize: 11, color: '#475569', fontWeight: '500' },
  breakdownBarBg: { flex: 1, height: 6, backgroundColor: '#E2E8F0', borderRadius: 3, marginHorizontal: 8 },
  breakdownBarFill: { height: '100%', borderRadius: 3 },
  breakdownScore: { width: 38, fontSize: 11, fontWeight: '700', textAlign: 'right' },
  breakdownNotes: { marginTop: 6, paddingTop: 6, borderTopWidth: 1, borderTopColor: '#E2E8F0' },
  breakdownNote: { fontSize: 11, color: '#64748B', lineHeight: 15 },

  skillsContainer: { flexDirection: 'row', flexWrap: 'wrap', marginBottom: 12 },
  skillBadge: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#F1F5F9', paddingHorizontal: 8, paddingVertical: 4,
    borderRadius: 4, marginRight: 6, marginBottom: 6,
  },
  matchedSkillBadge: { backgroundColor: '#D1FAE5', borderWidth: 1, borderColor: '#A7F3D0' },
  skillCheckmark: { marginRight: 4 },
  skillText: { fontSize: 11, color: '#334155' },
  matchedSkillText: { color: '#047857', fontWeight: '500' },

  jobActions: { flexDirection: 'row', justifyContent: 'space-between' },
  actionButton: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    paddingVertical: 8, paddingHorizontal: 10, borderRadius: 8,
    backgroundColor: '#F1F5F9', marginRight: 8, flex: 1,
  },
  actionIcon: { marginRight: 4 },
  actionButtonText: { fontSize: 13, fontWeight: '500', color: '#334155' },
  applyButton: { backgroundColor: '#0e4e96', marginRight: 0 },
  applyButtonText: { color: '#FFFFFF', fontWeight: '600', fontSize: 13 },

  newBadge: {
    position: 'absolute', top: 12, right: 12,
    backgroundColor: '#10b981', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4,
  },
  newBadgeText: { fontSize: 9, fontWeight: 'bold', color: '#FFFFFF' },

  loadingContainer: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 20 },
  loadingText: { marginTop: 12, fontSize: 15, color: '#64748b', textAlign: 'center' },
  errorContainer: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 20 },
  errorTitle: { fontSize: 20, fontWeight: 'bold', color: '#dc2626', marginTop: 12, marginBottom: 8 },
  errorDescription: { fontSize: 16, color: '#64748b', textAlign: 'center', marginBottom: 20 },
  retryButton: { backgroundColor: '#0e4e96', paddingVertical: 10, paddingHorizontal: 20, borderRadius: 8 },
  retryButtonText: { color: '#fff', fontSize: 16, fontWeight: '600' },

  emptyContainer: { alignItems: 'center', justifyContent: 'center', padding: 20, marginTop: 40 },
  emptyTitle: { fontSize: 18, fontWeight: 'bold', color: '#64748B', marginTop: 16, marginBottom: 8 },
  emptyText: { fontSize: 14, color: '#94A3B8', textAlign: 'center', marginBottom: 24 },
  updateProfileButton: { backgroundColor: '#0e4e96', paddingVertical: 10, paddingHorizontal: 20, borderRadius: 8 },
  updateProfileButtonText: { color: '#fff', fontSize: 16, fontWeight: '600' },

  modalOverlay: { flex: 1, backgroundColor: 'rgba(0, 0, 0, 0.5)', justifyContent: 'center', alignItems: 'center' },
  modalContainer: { backgroundColor: '#FFFFFF', borderRadius: 12, width: '92%', maxHeight: '85%', maxWidth: 500 },
  modalHeader: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    borderBottomWidth: 1, borderBottomColor: '#E2E8F0', paddingHorizontal: 16, paddingVertical: 12,
  },
  modalTitle: { fontSize: 18, fontWeight: 'bold', color: '#0f172a' },
  closeButton: { padding: 4 },
  modalContent: { padding: 16 },
  modalSubtitle: { fontSize: 14, color: '#64748B', marginBottom: 16 },

  // Completeness card in modal
  completenessCard: {
    backgroundColor: '#F8FAFC', borderRadius: 10, padding: 12, marginBottom: 18,
    borderWidth: 1, borderColor: '#E2E8F0',
  },
  completenessHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  completenessTitle: { fontSize: 14, fontWeight: '600', color: '#0f172a' },
  completenessPct: { fontSize: 18, fontWeight: '700' },
  completenessBarBg: { height: 6, backgroundColor: '#E2E8F0', borderRadius: 3, overflow: 'hidden' },
  completenessBarFill: { height: '100%' },
  editProfileLink: { marginTop: 10, alignItems: 'center' },
  editProfileLinkText: { color: '#0e4e96', fontSize: 13, fontWeight: '600' },

  portfolioGrid: { flexDirection: 'row', flexWrap: 'wrap', marginBottom: 18 },
  portfolioChip: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#F1F5F9', borderRadius: 8, paddingHorizontal: 10, paddingVertical: 8,
    marginRight: 8, marginBottom: 8, borderWidth: 1, borderColor: '#E2E8F0',
  },
  portfolioChipMissing: { backgroundColor: '#FAFAFA', borderStyle: 'dashed' },
  portfolioChipLabel: { fontSize: 12, color: '#0e4e96', fontWeight: '500', marginHorizontal: 6, maxWidth: 90 },

  analyzingContainer: { padding: 24, alignItems: 'center', justifyContent: 'center' },
  analyzingText: { marginTop: 16, fontSize: 15, color: '#64748B', textAlign: 'center' },
  insightHeader: {
    flexDirection: 'row', marginBottom: 16, paddingBottom: 16,
    borderBottomWidth: 1, borderBottomColor: '#E2E8F0',
  },
  insightLogo: { width: 50, height: 50, borderRadius: 8, marginRight: 12, backgroundColor: '#F1F5F9' },
  insightHeaderInfo: { flex: 1 },
  insightPosition: { fontSize: 16, fontWeight: 'bold', color: '#0f172a', marginBottom: 2 },
  insightCompany: { fontSize: 14, color: '#334155', marginBottom: 6 },
  insightMatchBadge: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12, alignSelf: 'flex-start' },
  insightMatchText: { fontSize: 12, fontWeight: '600' },
  insightSection: { marginBottom: 20 },
  insightSectionTitle: { fontSize: 16, fontWeight: '600', color: '#0f172a', marginBottom: 12 },
  insightItem: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 8 },
  insightItemText: { fontSize: 14, color: '#334155', marginLeft: 8, flex: 1 },
  gapAnalysisHeader: {
    flexDirection: 'row', paddingBottom: 8,
    borderBottomWidth: 1, borderBottomColor: '#E2E8F0', marginBottom: 8,
  },
  gapAnalysisHeaderText: { fontSize: 13, fontWeight: '600', color: '#64748B' },
  gapAnalysisRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  gapAnalysisSkill: { flex: 2, fontSize: 13, color: '#334155' },
  gapAnalysisImportance: { flex: 1, fontSize: 13, color: '#334155' },
  gapAnalysisStatus: { flex: 1, fontSize: 13, color: '#334155', textAlign: 'right' },
  importanceBadge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 4, flex: 1, alignItems: 'center' },
  highImportance: { backgroundColor: '#FEE2E2' },
  mediumImportance: { backgroundColor: '#FEF3C7' },
  lowImportance: { backgroundColor: '#E0F2FE' },
  importanceText: { fontSize: 11, fontWeight: '500' },
  missingSkill: { color: '#dc2626' },
  similarSkill: { color: '#f59e0b' },
  existingSkill: { color: '#16a34a' },
  addMissingSkillsButton: {
    backgroundColor: '#0e4e96', paddingVertical: 12, borderRadius: 8,
    alignItems: 'center', marginTop: 8, marginBottom: 16,
  },
  addMissingSkillsText: { color: '#FFFFFF', fontSize: 16, fontWeight: '600' },

  addSkillContainer: { backgroundColor: '#F8FAFC', padding: 12, borderRadius: 8, marginBottom: 20 },
  skillInput: {
    backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: '#E2E8F0', borderRadius: 8,
    paddingHorizontal: 12, paddingVertical: 10, fontSize: 15, marginBottom: 12,
  },
  skillLevelContainer: { marginBottom: 12 },
  skillLevelLabel: { fontSize: 13, fontWeight: '500', color: '#334155', marginBottom: 8 },
  skillLevelOptions: { flexDirection: 'row', justifyContent: 'space-between' },
  skillLevelOption: {
    flex: 1, paddingVertical: 8, alignItems: 'center',
    backgroundColor: '#F1F5F9', marginHorizontal: 2, borderRadius: 4,
  },
  selectedSkillLevel: { backgroundColor: '#0e4e96' },
  skillLevelText: { fontSize: 12, fontWeight: '500', color: '#64748B' },
  selectedSkillLevelText: { color: '#FFFFFF' },
  addSkillButton: { backgroundColor: '#0e4e96', paddingVertical: 10, borderRadius: 8, alignItems: 'center' },
  disabledButton: { backgroundColor: '#94A3B8' },
  addSkillButtonText: { color: '#FFFFFF', fontSize: 14, fontWeight: '600' },
  currentSkillsTitle: { fontSize: 15, fontWeight: '600', color: '#0f172a', marginBottom: 10, marginTop: 4 },
  currentSkillsList: { marginBottom: 20 },
  currentSkillItem: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: '#E2E8F0', borderRadius: 8,
    paddingHorizontal: 12, paddingVertical: 10, marginBottom: 8,
  },
  currentSkillInfo: { flexDirection: 'row', alignItems: 'center', flex: 1 },
  currentSkillName: { fontSize: 14, color: '#334155', flex: 1 },
  skillLevelBadge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 4, marginLeft: 8 },
  expertBadge: { backgroundColor: '#D1FAE5' },
  advancedBadge: { backgroundColor: '#E0F2FE' },
  intermediateBadge: { backgroundColor: '#FEF3C7' },
  beginnerBadge: { backgroundColor: '#F1F5F9' },
  skillLevelBadgeText: { fontSize: 12, fontWeight: '500' },
  removeSkillButton: { padding: 4 },
  updateMatchingButton: { backgroundColor: '#0e4e96', paddingVertical: 12, borderRadius: 8, alignItems: 'center', marginBottom: 16 },
  updateMatchingButtonText: { color: '#FFFFFF', fontSize: 16, fontWeight: '600' },
});
